import { useAtom } from 'jotai';
import * as state from '../state';
import { useCallback, useMemo } from 'react';
import { Transform } from '@fabric-web/3d-viewer';
import {
  SCANS_POSITION_PRECISION_FACTOR,
  SCANS_ROTATION_PRECISION_FACTOR,
  UNDO_COMBINE_CHANGES_INTERVAL_MSEC,
  UNDO_MAX_HISTORY_SIZE,
} from '../constants';

export const useScansTransform = () => {
  const [scansTransformWithHistory, setScansTransformWithHistory] = useAtom(
    state.scansTransformWithHistory
  );
  const [transformMode, setTransformMode] = useAtom(state.transformMode);

  const scansTransform = useMemo(() => {
    if (!scansTransformWithHistory) return undefined;
    return scansTransformWithHistory.history[scansTransformWithHistory.undoIndex];
  }, [scansTransformWithHistory]);

  const setScansTransform = useCallback(
    (scansTransform: Transform) => {
      setScansTransformWithHistory((current) => {
        if (!current) {
          return {
            history: [scansTransform],
            undoIndex: 0,
            lastUpdateTime: Date.now(),
            transformMode,
          };
        }
        const currentTime = Date.now();
        const { history, undoIndex, lastUpdateTime, transformMode: oldTransformMode } = current;
        let newHistory = [...history];
        // remove redo entries
        if (undoIndex < history.length - 1) {
          newHistory.splice(undoIndex + 1, history.length - undoIndex - 1);
        }
        const oldTransform = history[undoIndex];

        const isSameMode = (transformMode ?? oldTransformMode) === oldTransformMode;
        const isSame =
          isSameMode &&
          oldTransform.position.every(
            (value, index) =>
              Math.round(value * SCANS_POSITION_PRECISION_FACTOR) ===
              Math.round(scansTransform.position[index] * SCANS_POSITION_PRECISION_FACTOR)
          ) &&
          oldTransform.rotation.every(
            (value, index) =>
              Math.round(value * SCANS_ROTATION_PRECISION_FACTOR) ===
              Math.round(scansTransform.rotation[index] * SCANS_ROTATION_PRECISION_FACTOR)
          );

        if (
          isSameMode &&
          (isSame || currentTime < lastUpdateTime + UNDO_COMBINE_CHANGES_INTERVAL_MSEC)
        ) {
          if (isSame) {
            return current;
          }
          newHistory[undoIndex] = scansTransform;
        } else {
          if (newHistory.length >= UNDO_MAX_HISTORY_SIZE) {
            newHistory = newHistory.slice(0, UNDO_MAX_HISTORY_SIZE - 1);
          }
          newHistory.push(scansTransform);
        }
        return {
          history: newHistory,
          undoIndex: newHistory.length - 1,
          lastUpdateTime: currentTime,
          transformMode,
        };
      });
    },
    [setScansTransformWithHistory, transformMode]
  );

  const undoScansTransform = useCallback(() => {
    setTransformMode(undefined);
    setTimeout(
      () =>
        setScansTransformWithHistory((current) => {
          if (current && current.undoIndex > 0) {
            return { ...current, undoIndex: current.undoIndex - 1 };
          }
          return current;
        }),
      10
    );
  }, [setScansTransformWithHistory, setTransformMode]);

  const redoScansTransform = useCallback(() => {
    setTransformMode(undefined);
    setTimeout(
      () =>
        setScansTransformWithHistory((current) => {
          if (current && current.undoIndex < current.history.length - 1) {
            return { ...current, undoIndex: current.undoIndex + 1 };
          }
          return current;
        }),
      10
    );
  }, [setScansTransformWithHistory, setTransformMode]);

  return {
    scansTransform,
    setScansTransform,
    undoScansTransform,
    redoScansTransform,
    scansTransformWithHistory,
  };
};
